/*
  bb-form-meta.js — fills the hidden attribution block rendered by
  snippets/form-meta.liquid into every {% form 'contact' %} in the theme.

  Why this exists: a Shopify contact-form submission is emailed and then
  discarded. There is no Admin record, no metaobject, no API to query later.
  If the email doesn't say which page and which campaign produced the lead,
  that information is gone permanently.

  Two problems this solves that per-page scripts kept getting wrong:

  1. Attribution dies on navigation. Someone lands on
     /pages/garden-party?utm_source=instagram, browses to /pages/space-rental,
     then submits. Reading UTMs off location.search at submit time returns
     nothing. We snapshot campaign data into sessionStorage on the first
     tagged pageview and reuse it for the rest of the session.

  2. Same-origin referrers overwrite the real one. document.referrer after an
     internal click is our own domain, which is useless. We keep the first
     external referrer of the session instead.

  Empty fields are stripped on submit so the notification email never shows
  blank rows.

  Since 2026-08-17 this file also feeds the lead ledger: the answers of an
  accepted submission are POSTed to a Cloudflare Worker so the notification
  email stops being the only record of an inquiry. See the comment above
  stashPending() for how that capture stays honest.
*/
(function () {
  'use strict';

  var SESSION_KEY = 'bb:form-meta:touch';
  var FIRST_KEY = 'bb:form-meta:first';

  var LEDGER_URL = 'https://bloom-boom-lead-ledger.human-renaissance.workers.dev/v1/leads';
  var PENDING_KEY = 'bb:lead-ledger:pending';
  var PENDING_TTL_MS = 30 * 60 * 1000;
  var PENDING_MAX = 5;
  var META_KEYS = ['channel', 'campaign', 'click_id', 'referrer', 'first_visit', 'device'];
  var PAYLOAD_KEYS = [
    'lead_id',
    'form',
    'page',
    'page_url',
    'submitted_at_ms',
    'fields',
    'attribution'
  ];
  var POSTED_RE = /[?&]contact_posted=true(&|$)/;

  var CAMPAIGN_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  var CLICK_PARAMS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid', 'li_fat_id', 'epik'];

  /* Referrer host -> friendly channel label. Checked as a suffix match so
     www.google.com, google.co.uk and m.facebook.com all resolve. */
  var KNOWN_REFERRERS = [
    ['instagram.', 'Instagram'],
    ['facebook.', 'Facebook'],
    ['l.facebook.', 'Facebook'],
    ['google.', 'Google (organic)'],
    ['bing.', 'Bing (organic)'],
    ['duckduckgo.', 'DuckDuckGo (organic)'],
    ['yahoo.', 'Yahoo (organic)'],
    ['chatgpt.com', 'ChatGPT'],
    ['openai.com', 'ChatGPT'],
    ['perplexity.ai', 'Perplexity'],
    ['claude.ai', 'Claude'],
    ['gemini.google.', 'Gemini'],
    ['tiktok.', 'TikTok'],
    ['pinterest.', 'Pinterest'],
    ['linkedin.', 'LinkedIn'],
    ['lnkd.in', 'LinkedIn'],
    ['yelp.', 'Yelp'],
    ['peerspace.', 'Peerspace'],
    ['thebash.', 'The Bash'],
    ['eventbrite.', 'Eventbrite'],
    ['t.co', 'X / Twitter'],
    ['reddit.', 'Reddit']
  ];

  function storage(kind) {
    try {
      var s = window[kind];
      var probe = '__bb_probe__';
      s.setItem(probe, '1');
      s.removeItem(probe);
      return s;
    } catch (e) {
      /* Safari private mode and cookie-blocking extensions throw here. */
      return null;
    }
  }

  function readJSON(store, key) {
    if (!store) return null;
    try {
      return JSON.parse(store.getItem(key) || 'null');
    } catch (e) {
      return null;
    }
  }

  function writeJSON(store, key, value) {
    if (!store) return;
    try {
      store.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* Quota or private mode — attribution degrades, form still submits. */
    }
  }

  function paramsFromUrl() {
    var out = {};
    var search = window.location.search;
    if (!search || search.length < 2) return out;
    search
      .slice(1)
      .split('&')
      .forEach(function (pair) {
        if (!pair) return;
        var idx = pair.indexOf('=');
        var key = decodeURIComponent((idx === -1 ? pair : pair.slice(0, idx)).replace(/\+/g, ' '));
        var val = idx === -1 ? '' : decodeURIComponent(pair.slice(idx + 1).replace(/\+/g, ' '));
        if (key) out[key.toLowerCase()] = val;
      });
    return out;
  }

  function externalReferrer() {
    var ref = document.referrer || '';
    if (!ref) return '';
    try {
      if (new URL(ref).hostname === window.location.hostname) return '';
    } catch (e) {
      return '';
    }
    return ref;
  }

  /* Builds this pageview's touch, then merges it over whatever the session
     already knows. A tagged landing page wins; an untagged internal hop
     inherits. */
  function resolveTouch() {
    var session = storage('sessionStorage');
    var stored = readJSON(session, SESSION_KEY) || {};
    var params = paramsFromUrl();
    var touch = {};
    var tagged = false;

    CAMPAIGN_PARAMS.concat(CLICK_PARAMS).forEach(function (key) {
      if (params[key]) {
        touch[key] = params[key];
        tagged = true;
      }
    });

    var ref = externalReferrer();
    if (ref) touch.referrer = ref;

    if (!tagged) {
      /* Untagged pageview: keep the session's campaign data intact. */
      CAMPAIGN_PARAMS.concat(CLICK_PARAMS).forEach(function (key) {
        if (stored[key]) touch[key] = stored[key];
      });
    }
    if (!touch.referrer && stored.referrer) touch.referrer = stored.referrer;
    touch.landing = stored.landing || window.location.href;

    writeJSON(session, SESSION_KEY, touch);

    var durable = storage('localStorage');
    if (durable && !readJSON(durable, FIRST_KEY)) {
      writeJSON(durable, FIRST_KEY, {
        landing: window.location.href,
        referrer: touch.referrer || '',
        at: new Date().toISOString()
      });
    }

    return touch;
  }

  function channelLabel(touch) {
    if (touch.utm_source || touch.utm_medium) {
      var parts = [touch.utm_source || 'unknown', touch.utm_medium || 'unknown'];
      return parts.join(' / ');
    }
    if (touch.gclid || touch.gbraid || touch.wbraid) return 'Google Ads (paid search)';
    if (touch.fbclid) return 'Meta ads';
    if (touch.msclkid) return 'Microsoft Ads';
    if (touch.ttclid) return 'TikTok Ads';

    if (touch.referrer) {
      var host = '';
      try {
        host = new URL(touch.referrer).hostname.toLowerCase();
      } catch (e) {
        host = '';
      }
      for (var i = 0; i < KNOWN_REFERRERS.length; i++) {
        if (host.indexOf(KNOWN_REFERRERS[i][0]) !== -1) return KNOWN_REFERRERS[i][1];
      }
      if (host) return host.replace(/^www\./, '') + ' (referral)';
    }
    return 'Direct / unattributed';
  }

  function campaignLabel(touch) {
    var bits = [];
    if (touch.utm_campaign) bits.push(touch.utm_campaign);
    if (touch.utm_content) bits.push('content: ' + touch.utm_content);
    if (touch.utm_term) bits.push('term: ' + touch.utm_term);
    return bits.join(' · ');
  }

  function clickIdLabel(touch) {
    for (var i = 0; i < CLICK_PARAMS.length; i++) {
      var key = CLICK_PARAMS[i];
      if (touch[key]) return key + '=' + touch[key];
    }
    return '';
  }

  function firstVisitLabel() {
    var first = readJSON(storage('localStorage'), FIRST_KEY);
    if (!first || !first.landing) return '';
    var when = '';
    try {
      when = new Date(first.at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (e) {
      when = '';
    }
    var path = first.landing;
    try {
      path = new URL(first.landing).pathname;
    } catch (e) {
      /* keep the raw value */
    }
    return when ? path + ' on ' + when : path;
  }

  function deviceLabel() {
    var ua = navigator.userAgent || '';
    var device = 'Desktop';
    if (/iPad|Tablet/i.test(ua)) device = 'iPad / tablet';
    else if (/iPhone/i.test(ua)) device = 'iPhone';
    else if (/Android/i.test(ua)) device = /Mobile/i.test(ua) ? 'Android phone' : 'Android tablet';
    else if (/Mobile/i.test(ua)) device = 'Mobile';

    var browser = 'Browser';
    if (/Edg\//.test(ua)) browser = 'Edge';
    else if (/OPR\//.test(ua)) browser = 'Opera';
    else if (/Chrome\//.test(ua)) browser = 'Chrome';
    else if (/Firefox\//.test(ua)) browser = 'Firefox';
    else if (/Safari\//.test(ua)) browser = 'Safari';

    return device + ' · ' + browser;
  }

  function submittedLabel() {
    var now = new Date();
    try {
      return now.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
      });
    } catch (e) {
      return now.toISOString();
    }
  }

  function makeLeadId() {
    var stamp = Date.now().toString(36);
    var rand = Math.random().toString(36).slice(2, 8);
    return 'bb-' + stamp + '-' + rand;
  }

  function fill(block, key, value) {
    var field = block.querySelector('[data-bb-meta="' + key + '"]');
    if (field) field.value = value || '';
  }

  /* Removing the name attribute is enough — a nameless input is not submitted,
     and leaving the element in place keeps the DOM stable for anything else
     watching the form.

     Scope is the whole form, not just our block. The identity-scope forms
     carry tracker-owned hidden rows (Utm Term, Gclid, Gbraid, Wbraid, Fbclid,
     …) that space-rental-tracking.js, corporate-proposal-tracking.js and
     corporate-garden-party.js fill at page load and never touch again, so on
     an untagged visit they reach Shopify empty and print as blank rows in the
     notification email. A hidden contact[…] input is attribution plumbing by
     definition — the customer never sees it — and an empty one carries no
     information, so stripping it can only remove noise. Visible controls are
     left alone: an empty optional answer is the customer's, and Shopify's own
     validation must still see contact[email]. Non-contact hidden inputs
     (form_type, utf8, return_to) are untouched. */
  function prune(form) {
    var fields = form.querySelectorAll('input[type="hidden"]');
    for (var i = 0; i < fields.length; i++) {
      var name = fields[i].getAttribute('name') || '';
      if (name.indexOf('contact[') !== 0) continue;
      if (!fields[i].value) fields[i].removeAttribute('name');
    }
  }

  /* ------------------------------------------------------------------
     Lead ledger — pending on submit, sent on confirmed success

     Shopify POSTs the contact form, redirects, and re-renders the page with
     posted_successfully? set. Only that second render proves Shopify accepted
     the submission: a post that fails hCaptcha or field validation never gets
     there. So the submit handler only *stashes* the answers in sessionStorage,
     and the next page that reports a confirmed success ships them to the
     ledger Worker. Abandoned and rejected attempts never become records.

     The POST is text/plain because that keeps it a CORS-simple request — no
     preflight to survive on a page the browser is already unloading — and
     keepalive so the send still completes if the customer navigates away from
     the success page immediately. A send that fails leaves its entry in place;
     the next confirmed success inside the TTL retries it and the Worker
     dedupes on lead_id.

     Every step is wrapped: nothing here may disturb the submission itself or
     the attribution filling above.
  ------------------------------------------------------------------ */

  function readPending(session) {
    var list = readJSON(session, PENDING_KEY);
    return Array.isArray(list) ? list : [];
  }

  function fieldText(data, key) {
    var value = data.get(key);
    return typeof value === 'string' ? value : '';
  }

  function attributionFrom(block) {
    var out = {};
    META_KEYS.forEach(function (key) {
      var field = block.querySelector('[data-bb-meta="' + key + '"]');
      out[key] = (field && field.value) || '';
    });
    return out;
  }

  /* True when this field name belongs to the attribution block — one of our
     own rows rather than something the customer answered. A name shared with
     a control outside the block counts as the customer's. */
  function insideBlock(block, form, name) {
    var el = form.elements ? form.elements[name] : null;
    if (!el) return false;
    if (el.nodeType) return block.contains(el);
    for (var i = 0; i < el.length; i++) {
      if (!block.contains(el[i])) return false;
    }
    return el.length > 0;
  }

  function stripWrapper(name) {
    var match = /^contact\[(.*)\]$/.exec(name);
    return match ? match[1] : name;
  }

  function pageUrlFallback() {
    return (window.location.href || '').split('#')[0].split('?')[0];
  }

  function buildPending(form, block, metaLeadId) {
    var data = new FormData(form);
    var fields = [];
    var now = Date.now();

    data.forEach(function (value, key) {
      if (key.indexOf('contact[') !== 0) return;
      /* FormData yields a File for file inputs; only text belongs in the
         ledger, and an empty answer is not worth a row. */
      if (typeof value !== 'string' || !value) return;
      if (insideBlock(block, form, key)) return;
      fields.push([stripWrapper(key), value]);
    });

    return {
      /* Identity-scope forms carry their own Lead ID (wired to GA4/Google Ads);
         full-scope forms get the one this script generated. Either way it is
         the same field name, so FormData settles the precedence. */
      lead_id: fieldText(data, 'contact[Lead ID]') || metaLeadId,
      form: fieldText(data, 'contact[Form]'),
      page: fieldText(data, 'contact[Page]'),
      page_url: fieldText(data, 'contact[Page URL]') || pageUrlFallback(),
      submitted_at_ms: now,
      fields: fields,
      attribution: attributionFrom(block),
      stashed_at: now
    };
  }

  function stashPending(form, block, metaLeadId) {
    try {
      var session = storage('sessionStorage');
      if (!session) return;
      var entry = buildPending(form, block, metaLeadId);
      /* A retry supersedes the earlier stash of the same form — otherwise a
         customer who fixes a validation error files two leads. */
      var list = readPending(session).filter(function (item) {
        return item && item.form !== entry.form;
      });
      list.push(entry);
      if (list.length > PENDING_MAX) list = list.slice(list.length - PENDING_MAX);
      writeJSON(session, PENDING_KEY, list);
    } catch (e) {
      /* Ledger capture is best-effort; the submission continues regardless. */
    }
  }

  function confirmedSuccess() {
    /* Shopify redirects an accepted post to return_to (every form's carries
       contact_posted=true) or, without one, back to the page with
       ?contact_posted=true. A server-rendered marker is not available: the
       snippet is rendered with {% render %}, which cannot see
       form.posted_successfully?. */
    if (POSTED_RE.test(window.location.search || '')) return true;
    try {
      var nav = window.performance && window.performance.getEntriesByType
        ? window.performance.getEntriesByType('navigation')
        : null;
      /* The pre-replaceState URL, for pages that clean the query early. */
      if (nav && nav.length && nav[0] && POSTED_RE.test(nav[0].name || '')) return true;
    } catch (e) {
      /* No navigation timing — the two checks above stand on their own. */
    }
    return false;
  }

  function payloadFor(entry) {
    var payload = {};
    /* Explicit key list: stashed_at is bookkeeping and the Worker rejects
       unknown top-level keys. */
    PAYLOAD_KEYS.forEach(function (key) {
      payload[key] = entry[key];
    });
    payload.success_url = window.location.href;
    return payload;
  }

  function removePending(session, leadId) {
    /* Re-read rather than splice a captured list: several sends can settle
       against the same storage key. */
    writeJSON(
      session,
      PENDING_KEY,
      readPending(session).filter(function (item) {
        return item && item.lead_id !== leadId;
      })
    );
  }

  function sendPending(session, entry) {
    try {
      window
        .fetch(LEDGER_URL, {
          method: 'POST',
          mode: 'cors',
          keepalive: true,
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body: JSON.stringify(payloadFor(entry))
        })
        .then(function (response) {
          if (response && response.ok) removePending(session, entry.lead_id);
        })
        .catch(function () {
          /* Kept for the next confirmed success within the TTL. */
        });
    } catch (e) {
      /* Same: the entry stays pending. */
    }
  }

  function formsOnPage(blocks) {
    var names = [];
    for (var i = 0; i < blocks.length; i++) {
      var input = blocks[i].querySelector && blocks[i].querySelector('input[name="contact[Form]"]');
      if (input && input.value) names.push(input.value);
    }
    return names;
  }

  function flushPending(blocks) {
    try {
      if (!confirmedSuccess()) return;
      var session = storage('sessionStorage');
      if (!session) return;

      var stored = readPending(session);
      if (!stored.length) return;

      var cutoff = Date.now() - PENDING_TTL_MS;
      var list = stored.filter(function (item) {
        return item && typeof item.stashed_at === 'number' && item.stashed_at >= cutoff;
      });
      /* Expired answers are dropped from storage even when nothing is sent. */
      if (list.length !== stored.length) writeJSON(session, PENDING_KEY, list);

      if (!list.length || !window.fetch) return;
      /* When the success page still renders form-meta blocks, only entries for
         those forms are eligible, so an abandoned attempt on another form
         cannot ride along. Some success pages swap the form for a thank-you
         card (space rental, corporate) and render no block at all — then every
         fresh pending entry is sent; the Worker dedupes by lead id. */
      var pageForms = formsOnPage(blocks);
      list.forEach(function (entry) {
        if (pageForms.length && pageForms.indexOf(entry.form) === -1) return;
        sendPending(session, entry);
      });
    } catch (e) {
      /* Nothing sent, nothing lost — the entries stay for the next success. */
    }
  }

  function init() {
    var blocks = document.querySelectorAll('[data-bb-form-meta]');
    /* Flush first: a confirmed success page may render no form-meta block at
       all (the section swaps the form for a thank-you card), and the pending
       submission from the previous page must still reach the ledger. */
    flushPending(blocks);
    if (!blocks.length) return;

    var touch = resolveTouch();
    var channel = channelLabel(touch);
    var campaign = campaignLabel(touch);
    var clickId = clickIdLabel(touch);
    var firstVisit = firstVisitLabel();
    var device = deviceLabel();

    Array.prototype.forEach.call(blocks, function (block) {
      if (block.dataset.bbFormMetaInit === '1') return;
      block.dataset.bbFormMetaInit = '1';

      fill(block, 'channel', channel);
      fill(block, 'campaign', campaign);
      fill(block, 'click_id', clickId);
      fill(block, 'referrer', touch.referrer || '');
      fill(block, 'first_visit', firstVisit);
      fill(block, 'device', device);

      /* Held in a variable as well as the input: identity-scope blocks have no
         Lead ID row, and the ledger still needs an id for those submissions. */
      var metaLeadId = makeLeadId();
      fill(block, 'lead_id', metaLeadId);

      var form = block.closest ? block.closest('form') : null;
      if (!form) return;

      /* Stamped at submit rather than page load so a form left open in a tab
         for an hour still reports the time the customer actually sent it. */
      form.addEventListener('submit', function () {
        fill(block, 'submitted', submittedLabel());
        prune(form);
        stashPending(form, block, metaLeadId);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
